/**
 * The `#k=…` handoff, on its own so `main.tsx` can call it without pulling the
 * capability tables into the MAIN chunk, which the public board and the OBS
 * overlay paint from, and which has no business carrying auth logic.
 *
 * The token lives in a module variable for the lifetime of one page load and
 * nowhere else: not localStorage, not the URL after this runs, not a WebSocket
 * query string.
 *
 * TWO kinds of link carry a `#k=` (V2 Phase 3): a grant link, which any gated
 * page trades for a session at once, and an INVITATION, which only `/invite`
 * reads and which is spent by a form, not by the first render. The path says
 * which; the invite token stays readable until the acceptance succeeds,
 * because the page may have to send its holder to `/login` and back first.
 *
 * A THIRD thing rides the fragment since V3 Phase 1: a Telegram Mini App
 * launch (`#tgWebAppData=…`, what `telegram-web-app.js` itself reads off
 * `location.hash`). It is lifted the same way, kept in memory, and traded for
 * the account cookie by `lib/session.ts`; its `startapp` parameter is routed
 * HERE, before render, so the router boots on the page it names.
 */
import { miniAppPath, pickupTokenOfStartParam } from "../../shared/mini-app";

let bootstrapToken: string | null = null;
let inviteToken: string | null = null;
/** A squad's pickup invite link (2026-09-15): only `/pickup-invite` reads it. */
let pickupToken: string | null = null;
/** A support chat's link token (2026-09-16): only `/support/<id>` reads it,
 *  and trades it for the `score_sc` cookie once. */
let supportToken: string | null = null;

export interface TelegramLaunch {
  /** The signed query string, as Telegram wrote it. */
  initData: string;
  startParam?: string;
}

let telegramLaunch: TelegramLaunch | null = null;

/** `newKeyId()` is always 12 characters, which is what lets a deep link carry
 *  a token without its dot (`shared/telegram.ts`, `inviteStartPayload`). */
const KEY_ID_LENGTH = 12;

/**
 * Where a Mini App's `startapp` parameter lands: an invitation goes to the
 * invite page with its token restored, a club to its public profile. The
 * rules are `parseStartPayload`'s, repeated here in two lines so the main
 * chunk does not import `shared/telegram.ts` (and `shared/auth.ts` with it).
 */
function startParamPath(param: string | undefined): string | null {
  const route = miniAppPath(param);
  if (route !== null) return route;
  const pickup = pickupTokenOfStartParam(param);
  if (pickup !== null) {
    pickupToken = pickup;
    return "/pickup-invite";
  }
  if (param === undefined || !/^[A-Za-z0-9_-]{1,64}$/.test(param)) return null;
  if (param.startsWith("i") && param.length > 1 + KEY_ID_LENGTH) {
    inviteToken = `${param.slice(1, 1 + KEY_ID_LENGTH)}.${param.slice(1 + KEY_ID_LENGTH)}`;
    return "/invite";
  }
  if (param.startsWith("club_") && param.length > "club_".length) {
    return `/teams/${param.slice("club_".length)}`;
  }
  return null;
}

/** Read `#k=…` (and a Telegram launch) and strip them from the address bar.
 *  Called once, before render. */
export function takeBootstrapToken(): void {
  const hash = window.location.hash;
  let path = window.location.pathname;
  let strip = false;
  const m = /[#&]k=([^&]+)/.exec(hash);
  if (m?.[1] !== undefined) {
    const token = decodeURIComponent(m[1]);
    if (path === "/invite") inviteToken = token;
    else if (path === "/pickup-invite") pickupToken = token;
    else if (path.startsWith("/support/")) supportToken = token;
    else bootstrapToken = token;
    strip = true;
  }
  const launch = /[#&]tgWebAppData=([^&]+)/.exec(hash);
  if (launch?.[1] !== undefined) {
    const start = /[#&]tgWebAppStartParam=([^&]+)/.exec(hash)?.[1];
    const initData = decodeURIComponent(launch[1]);
    const startParam =
      start === undefined
        ? (new URLSearchParams(initData).get("start_param") ?? undefined)
        : decodeURIComponent(start);
    // Only SDK presentation metadata survives reload/native page navigation.
    // Never persist signed initData, identity, invitations or credentials.
    try {
      const params = new URLSearchParams(hash.slice(1));
      const ui = Object.fromEntries(
        ["tgWebAppVersion", "tgWebAppPlatform", "tgWebAppThemeParams"].flatMap((key) =>
          params.has(key) ? [[key, params.get(key)]] : [],
        ),
      );
      sessionStorage.setItem("__telegram__initParams", JSON.stringify(ui));
      sessionStorage.setItem("score.miniapp", "1");
    } catch {
      /* The web navigation still works without storage. */
    }
    telegramLaunch = {
      initData,
      ...(startParam !== undefined ? { startParam } : {}),
    };
    path = startParamPath(startParam) ?? path;
    strip = true;
  }
  if (strip) history.replaceState(null, "", path + window.location.search);
}

export function isMiniApp(): boolean {
  if (telegramLaunch !== null) return true;
  try {
    return sessionStorage.getItem("score.miniapp") === "1";
  } catch {
    return false;
  }
}

/** Hand the token over exactly once; a second read gets nothing. */
export function consumeBootstrapToken(): string | null {
  const token = bootstrapToken;
  bootstrapToken = null;
  return token;
}

export function setBootstrapToken(token: string): void {
  bootstrapToken = token;
}

/** The invitation this page load arrived with, if any. Not consumed by the
 *  read: the page shows it, may leave for `/login` and come back, and only
 *  `clearInviteToken` forgets it. */
export function peekInviteToken(): string | null {
  return inviteToken;
}

export function setInviteToken(token: string): void {
  inviteToken = token;
}

export function clearInviteToken(): void {
  inviteToken = null;
}

/** The pickup link this page load arrived with. Kept across a sign-in; `/pickup-invite` clears it once used. */
export function peekPickupToken(): string | null {
  return pickupToken;
}

export function clearPickupToken(): void {
  pickupToken = null;
}

/** The support chat link this page load arrived with; the chat page spends
 *  it on the cookie and clears it. */
export function peekSupportToken(): string | null {
  return supportToken;
}

export function clearSupportToken(): void {
  supportToken = null;
}

/** The Mini App launch this page load arrived with, if any. Not consumed by
 *  the read: an account already signed in keeps it for `/account`'s
 *  one-tap connect; the session exchange consumes it. */
export function peekTelegramLaunch(): TelegramLaunch | null {
  return telegramLaunch;
}

export function consumeTelegramLaunch(): TelegramLaunch | null {
  const launch = telegramLaunch;
  telegramLaunch = null;
  return launch;
}
